<template>
  <div class="entity-browser-wrapper">
    <div class="header-ribbon">Company Attributes</div>
    <section class="entity-browser">
      <!-- Sidebar -->
      <div class="sidebar">
        <!-- Facilities Section -->
        <div class="sidebar-section">
          <div class="sidebar-header" @click="toggleSection('facilities')">
            <i class="fa fa-caret-right sidebar-caret" :class="{ expanded: expandedSections.facilities }"></i>
            Facilities <span class="text-muted sidebar-count">({{ sidebarData.facilities.length }})</span>
            <span v-if="loading" class="loading-indicator"><i class="fa fa-cog fa-spin"></i></span>
          </div>
          <div v-if="expandedSections.facilities" class="sidebar-items">
            <div v-if="sidebarData.facilities.length === 0 && !loading" class="no-items">
              No facilities found
            </div>
            <div
              v-for="facility in sidebarData.facilities"
              :key="facility.id"
              class="sidebar-item"
              :class="{ active: selectedItem?.id === facility.id, deactivated: isDeactivated(facility) }"
              @click="selectItem(facility, 'facilities')"
            >
              <span class="item-name">{{ facility.name }}</span>
              <span v-if="facility.badge" class="item-badge">{{ facility.badge }}</span>
            </div>
          </div>
        </div>

        <!-- Machines Section -->
        <div class="sidebar-section">
          <div class="sidebar-header" @click="toggleSection('machines')">
            <i class="fa fa-caret-right sidebar-caret" :class="{ expanded: expandedSections.machines }"></i>
            Machines <span class="text-muted sidebar-count">({{ sidebarData.machines.length }})</span>
            <span v-if="loading" class="loading-indicator"><i class="fa fa-cog fa-spin"></i></span>
          </div>
          <div v-if="expandedSections.machines" class="sidebar-items">
            <div v-if="sidebarData.machines.length === 0 && !loading" class="no-items">
              No machines found
              <span class="no-items-note">
                Imaging systems are no longer collected:<br>
                expected, not missing data.
              </span>
            </div>
            <div
              v-for="machine in sidebarData.machines"
              :key="machine.id"
              class="sidebar-item"
              :class="{ active: selectedItem?.id === machine.id, deactivated: isDeactivated(machine) }"
              @click="selectItem(machine, 'machines')"
            >
              <span class="item-name">{{ machine.name }}</span>
              <span v-if="machine.badge" class="item-badge">{{ machine.badge }}</span>
            </div>
          </div>
        </div>

        <!-- All Staff Section -->
        <div class="sidebar-section">
          <div class="sidebar-header" @click="toggleSection('staff')">
            <i class="fa fa-caret-right sidebar-caret" :class="{ expanded: expandedSections.staff }"></i>
            All Staff <span class="text-muted sidebar-count">({{ sidebarData.staff.length }})</span>
            <span v-if="loading" class="loading-indicator"><i class="fa fa-cog fa-spin"></i></span>
          </div>
          <div v-if="expandedSections.staff" class="sidebar-items">
            <div v-if="sidebarData.staff.length === 0 && !loading" class="no-items">
              No staff members found
            </div>
            <div
              v-for="staff in sidebarData.staff"
              :key="staff.id"
              class="sidebar-item"
              :class="{ active: selectedItem?.id === staff.id, deactivated: isDeactivated(staff) }"
              @click="selectItem(staff, 'staff')"
            >
              <span class="item-name">{{ staff.name }}</span>
              <span v-if="staff.badge" class="item-badge">{{ staff.badge }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="content-area">
        <div v-if="selectedItem && currentMetadata && !loadingDetails">
          <div class="content-header">
            <h2 :class="{ deactivated: isDeactivated(selectedItem) }">{{ selectedItem.name }}</h2>
            <div class="content-header-actions">
              <span v-if="isDeactivated(selectedItem)" class="content-badge content-badge-deactivated">
                Deactivated
              </span>
              <span v-if="selectedItem.badge" class="content-badge">{{ selectedItem.badge }}</span>
              <button
                v-if="selectedItemType === 'facilities' && isCompanyAccredited"
                class="button button-secondary button-small ml-1"
                @click="handleDownloadCertificate"
                :disabled="isDownloading"
                title="Download Certificate"
              >
                <i v-if="isDownloading" class="fa fa-spinner fa-spin"></i>
                <i v-else class="fa fa-download"></i>
                {{ isDownloading ? 'Downloading...' : 'Download Certificate' }}
              </button>
              <a
                v-if="isAdmin"
                class="button button-primary button-small ml-1"
                @click="viewEntry(selectedItem.id)"
                title="View Entry in Formidable"
              >
                View Entry
              </a>
              <button
                class="button button-small ml-1"
                :class="isDeactivated(selectedItem) ? 'button-activate' : 'button-deactivate'"
                @click="toggleActivation(selectedItem)"
                :disabled="togglingActivation"
                :title="isDeactivated(selectedItem) ? 'Activate' : 'Deactivate'"
              >
                <i v-if="togglingActivation" class="fa fa-spinner fa-spin"></i>
                <i v-else :class="isDeactivated(selectedItem) ? 'fa fa-toggle-on' : 'fa fa-toggle-off'"></i>
                {{ isDeactivated(selectedItem) ? 'Activate' : 'Deactivate' }}
              </button>
            </div>
          </div>
          
          <div v-if="activationError" class="activation-error">
            {{ activationError }}
          </div>

          <div class="metadata-container">
            <div 
              v-for="(fieldData, fieldKey) in currentMetadata" 
              :key="fieldKey"
              class="metadata-item"
            >
              <div class="metadata-question">{{ fieldData.label }}:</div>
              <div class="metadata-answer">{{ fieldData.value || 'Not specified' }}</div>
            </div>
          </div>

          <!-- Interview Date (admin only, staff only) -->
          <div v-if="isAdmin && selectedItemType === 'staff'" class="interview-date-container">
            <h3 class="interview-date-title">Interview Date</h3>
            <div class="interview-date-controls">
              <input
                type="date"
                class="interview-date-input"
                v-model="interviewDateDraft"
                :disabled="savingInterviewDate"
              />
              <button
                class="button button-primary button-small"
                @click="saveInterviewDate"
                :disabled="savingInterviewDate || !interviewDateChanged"
              >
                <i v-if="savingInterviewDate" class="fa fa-spinner fa-spin"></i>
                {{ savingInterviewDate ? 'Saving...' : 'Save' }}
              </button>
              <button
                v-if="interviewDateDraft"
                class="button button-secondary button-small"
                @click="interviewDateDraft = ''"
                :disabled="savingInterviewDate"
              >
                Clear
              </button>
            </div>
            <div v-if="interviewDateError" class="interview-date-error">
              {{ interviewDateError }}
            </div>
            <div v-else-if="interviewDateSaved" class="interview-date-saved">
              Interview date saved.
            </div>
            <div class="interview-date-note">
              Visible to administrators only.
            </div>
          </div>

          <!-- Associations Section -->
          <div v-if="currentAssociations && Object.keys(currentAssociations).length > 0" class="associations-container">
            <h3 class="associations-title">Related Items</h3>
            <div 
              v-for="(association, key) in currentAssociations" 
              :key="key"
              class="association-section"
            >
              <h4 class="association-title">{{ association.title }}</h4>
              <div v-if="association.entries && association.entries.length > 0" class="association-items">
                <div 
                  v-for="entry in association.entries" 
                  :key="entry.id"
                  class="association-item"
                >
                  <span class="association-name">{{ entry.display_attributes?.name || entry.name }}</span>
                  <span v-if="entry.display_attributes?.role" class="association-badge">
                    {{ Array.isArray(entry.display_attributes.role) ? entry.display_attributes.role[0] : entry.display_attributes.role }}
                  </span>
                </div>
              </div>
              <div v-else class="no-associations">
                No {{ association.title.toLowerCase() }} assigned
                <span v-if="deprecatedAssociationNotes[key]" class="no-associations-note">
                  {{ deprecatedAssociationNotes[key] }}<br>
                  expected, not missing data.
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else-if="loadingDetails" class="loading-state">
          <h3>Loading Details...</h3>
          <p>Please wait while we load the metadata.</p>
        </div>
        
        <div v-else-if="loading" class="loading-state">
          <h3>Loading...</h3>
          <p>Please wait while we load the entity data.</p>
        </div>
        
        <div v-else class="no-selection">
          <h3>Select an item to view details</h3>
          <p>Choose a staff member, facility, or machine from the sidebar to view its metadata.</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useCertificateDownload } from '../../composables/useCertificateDownload.js';
import { useLocalized } from '../../composables/useLocalized.js';

export default {
  name: 'EntityBrowser',
  props: {
    companyId: {
      type: [Number, String],
      required: true
    },
    entryPath: {
      type: String,
      default: ''
    },
    isCompanyAccredited: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { isDownloading, downloadError, downloadCertificate } = useCertificateDownload();
    const { isAdmin, entityRestUrl, restNonce } = useLocalized();

    return {
      isDownloading,
      downloadError,
      downloadCertificate,
      isAdmin,
      entityRestUrl,
      restNonce
    };
  },
  data() {
    return {
      selectedItem: null,
      selectedItemType: null,
      loadingDetails: false,
      error: null,
      expandedSections: {
        staff: true,
        facilities: true,
        machines: true
      },
      sidebarData: {
        staff: [],
        facilities: [],
        machines: []
      },
      loading: false,
      metadataStore: {},
      associationsStore: {},
      // Associations no longer required by the standards. An empty list for
      // these is the expected state, so say so rather than leaving an admin
      // wondering whether data is missing.
      deprecatedAssociationNotes: {
        machines: 'Imaging systems are no longer collected:',
        facilities: 'This association is no longer required:'
      },
      interviewDateStore: {},
      interviewDateDraft: '',
      savingInterviewDate: false,
      interviewDateError: null,
      interviewDateSaved: false,
      togglingActivation: false,
      activationError: null
    }
  },
  computed: {
    // The company routes live alongside entity-browser under the same REST
    // namespace. Derived from entityRestUrl because the two contexts localize
    // different roots: client gets the namespace root, admin gets .../admin.
    companyRestUrl() {
      return String(this.entityRestUrl || '').replace(/\/entity-browser\/?$/, '') + '/company'
    },

    currentMetadata() {
      return this.selectedItem ? this.metadataStore[this.selectedItem.id] : null
    },
    currentAssociations() {
      return this.selectedItem ? this.associationsStore[this.selectedItem.id] : null
    },
    savedInterviewDate() {
      if (!this.selectedItem) return ''
      return this.interviewDateStore[this.selectedItem.id] || ''
    },
    interviewDateChanged() {
      return this.interviewDateDraft !== this.savedInterviewDate
    }
  },
  watch: {
    companyId: {
      handler(newCompanyId) {
        if (newCompanyId) {
          // Clear current selection when company changes
          this.selectedItem = null
          this.selectedItemType = null
          this.metadataStore = {}
          this.associationsStore = {}
          
          // Load new company data
          this.loadCompanyEntities()
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.initializeData()
  },
  methods: {
    // Method to download certificate (uses ghost template)
    async handleDownloadCertificate() {
      if (!this.selectedItem || !this.selectedItem.id) return;

      // Use ghost template for admin downloads
      await this.downloadCertificate(this.selectedItem.id, true);
    },

    // Method to view entry in new tab
    viewEntry(entryId) {
      if (!entryId || !this.entryPath) return
      const url = `${this.entryPath}&id=${entryId}`
      window.open(url, '_blank')
    },

    // Sync the datepicker with whatever is stored for the selected staff member
    resetInterviewDateState() {
      this.interviewDateDraft = this.savedInterviewDate
      this.interviewDateError = null
      this.interviewDateSaved = false
    },

    saveInterviewDate() {
      if (!this.selectedItem || !this.isAdmin) return

      const staffId = this.selectedItem.id
      const value = this.interviewDateDraft || ''

      this.savingInterviewDate = true
      this.interviewDateError = null
      this.interviewDateSaved = false

      $.ajax({
        url: this.entityRestUrl + '/staff/' + staffId + '/interview-date',
        method: 'POST',
        beforeSend: (xhr) => {
          xhr.setRequestHeader('X-WP-Nonce', this.restNonce)
        },
        data: { interview_date: value },
        dataType: 'json',
        success: (result) => {
          if (result.success) {
            this.interviewDateStore[staffId] = result.data.interview_date || ''
            // Only surface the confirmation if the admin is still on this record
            if (this.selectedItem && this.selectedItem.id === staffId) {
              this.interviewDateDraft = this.interviewDateStore[staffId]
              this.interviewDateSaved = true
            }
          } else {
            this.interviewDateError = result.message || 'Failed to save the interview date.'
          }
          this.savingInterviewDate = false
        },
        error: (xhr, status, errorThrown) => {
          this.interviewDateError = xhr.responseJSON?.message || errorThrown || 'Network error'
          this.savingInterviewDate = false
        }
      })
    },

    // AJAX Methods
    loadCompanyEntities() {
      if (!this.companyId) return

      this.loading = true
      this.error = null
      
      $.ajax({
        url: this.entityRestUrl + '/company/' + this.companyId + '/entities',
        method: 'GET',
        beforeSend: (xhr) => {
          xhr.setRequestHeader('X-WP-Nonce', this.restNonce)
        },
        dataType: 'json',
        success: (result) => {
          if (result.success) {
            this.sidebarData.staff = result.data.staff || []
            this.sidebarData.facilities = result.data.facilities || []
            this.sidebarData.machines = result.data.machines || []
          } else {
            this.error = 'Failed to load entities: ' + (result.message || 'Unknown error')
            console.error('API Error:', result)
          }
          this.loading = false
        },
        error: (xhr, status, errorThrown) => {
          const errorMsg = xhr.responseJSON?.message || errorThrown || 'Network error'
          this.error = 'Error loading entities: ' + errorMsg
          console.error('AJAX Error:', xhr, status, errorThrown)
          this.loading = false
        }
      })
    },

    loadEntityDetails(item, type) {
      if (!item || !item.id) return

      // Map the type to the correct endpoint
      const endpointMap = {
        'staff': 'staff',
        'facilities': 'facilities', 
        'machines': 'machines'
      }
      
      const endpoint = endpointMap[type]
      if (!endpoint) {
        console.error('Unknown entity type:', type)
        return
      }

      this.loadingDetails = true
      
      $.ajax({
        url: this.entityRestUrl + '/' + endpoint + '/' + item.id,
        method: 'GET',
        beforeSend: (xhr) => {
          xhr.setRequestHeader('X-WP-Nonce', this.restNonce)
        },
        dataType: 'json',
        success: (result) => {
          if (result.success) {
            // Store the metadata for display
            this.metadataStore[item.id] = result.data.metadata
            // Store the associations for display
            this.associationsStore[item.id] = result.data.associations
            // Interview date is only present for administrators
            this.interviewDateStore[item.id] = result.data.interview_date || ''
            this.resetInterviewDateState()
          } else {
            this.error = 'Failed to load entity details: ' + (result.message || 'Unknown error')
            console.error('API Error:', result)
          }
          this.loadingDetails = false
        },
        error: (xhr, status, errorThrown) => {
          const errorMsg = xhr.responseJSON?.message || errorThrown || 'Network error'
          this.error = 'Error loading entity details: ' + errorMsg
          console.error('AJAX Error:', xhr, status, errorThrown)
          this.loadingDetails = false
        }
      })
    },

    toggleSection(section) {
      this.expandedSections[section] = !this.expandedSections[section]
    },

    // The sidebar list endpoints send a `deactivated` flag for every entity;
    // selectedItem is the sidebar object itself, so this reads the same way
    // for a list row and for the selected record.
    isDeactivated(item) {
      return !!(item && item.deactivated)
    },

    // Activate/deactivate the selected record. Hits the same
    // /company/toggle-activation route the listing pages use, so the server
    // side permission check and revision logging are shared.
    toggleActivation(item) {
      if (!item || this.togglingActivation) return

      const action = this.isDeactivated(item) ? 'activate' : 'deactivate'
      const label = item.name || 'this record'

      if (!confirm(`Are you sure you want to ${action} ${label}?`)) {
        return
      }

      const itemId = item.id
      this.togglingActivation = true
      this.activationError = null

      $.ajax({
        url: this.companyRestUrl + '/toggle-activation',
        method: 'POST',
        beforeSend: (xhr) => {
          xhr.setRequestHeader('X-WP-Nonce', this.restNonce)
        },
        data: { entity_id: itemId, action: action },
        dataType: 'json',
        success: (result) => {
          if (!result.success) {
            this.activationError = result.message || `Failed to ${action} ${label}.`
            return
          }

          // Update in place rather than reloading: the sidebar row and the
          // header badge both read the same flag, so both follow.
          const nowDeactivated = action === 'deactivate'
          this.applyActivationState(itemId, nowDeactivated)
        },
        error: (xhr, status, error) => {
          this.activationError = `Failed to ${action} ${label}: ${error}`
        },
        complete: () => {
          this.togglingActivation = false
        }
      })
    },

    // Write the new flag onto every copy of the record we hold: the sidebar
    // list entry and, when it is still the open one, selectedItem.
    applyActivationState(itemId, deactivated) {
      Object.keys(this.sidebarData).forEach((type) => {
        const match = this.sidebarData[type].find((row) => row.id === itemId)
        if (match) {
          match.deactivated = deactivated
        }
      })

      if (this.selectedItem && this.selectedItem.id === itemId) {
        this.selectedItem.deactivated = deactivated
      }
    },

    selectItem(item, type) {
      this.selectedItem = item
      this.selectedItemType = type
      
      // Clear any previous errors
      this.error = null
      this.activationError = null

      // Sync the interview datepicker to the newly selected record straight
      // away so a stale draft never carries over between staff members
      this.resetInterviewDateState()

      // Load detailed metadata when an item is selected
      this.loadEntityDetails(item, type)
    },

    // Load entities when component mounts or company changes
    initializeData() {
      if (this.companyId) {
        this.loadCompanyEntities()
      }
    }
  }
}
</script>